import React, { useEffect, useState } from "react";
import { Image, LoaderCircle, Trash2, Upload } from "lucide-react";
import MuxPlayer from "@mux/mux-player-react";
import MuxUploader from "@mux/mux-uploader-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import MediaGalleryPicker from "../MediaGalleryPicker";
import { btnGhost, btnPrimary, inputCls } from "./shared";

const STATUSES = [
  ["draft", "Draft"],
  ["ready", "Ready"],
  ["published", "Published"],
  ["hidden", "Hidden"],
];
const teaserButtonClass = "min-h-11 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-pink-400";

function detail(error, fallback) {
  const message = error?.response?.data?.detail;
  return typeof message === "string" ? message : fallback;
}

export default function TeaserSection({ videoId }) {
  const [teaser, setTeaser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [uploadInfo, setUploadInfo] = useState(null);
  const [libraryAssets, setLibraryAssets] = useState([]);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [posterOpen, setPosterOpen] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    api.get(`/api/mux/teaser/${videoId}`).then(({ data }) => {
      if (!active) return;
      setTeaser(data && typeof data === "object" && !Array.isArray(data) ? data : null);
    }).catch((requestError) => {
      if (!active) return;
      setError(requestError?.response?.status === 403
        ? "You do not have permission to manage this teaser."
        : detail(requestError, "Teaser could not be synced. Check your connection and retry."));
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [videoId]);

  const refresh = async () => {
    try {
      const { data } = await api.get(`/api/mux/teaser/status/${videoId}`);
      setTeaser(data && typeof data === "object" ? data : null);
      setError("");
      if (data?.mux_status === "ready") toast.success("Teaser media is ready");
      else toast.info("Teaser media is still processing");
    } catch (requestError) {
      setError(detail(requestError, "Teaser status could not be synced."));
    }
  };

  const startUpload = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/api/mux/teaser/upload", { video_id: videoId });
      setUploadInfo({ ...data, url: data.upload_url || data.url });
      const current = await api.get(`/api/mux/teaser/${videoId}`);
      setTeaser(current.data || null);
    } catch (requestError) {
      setError(detail(requestError, "Teaser upload could not be started."));
    } finally { setBusy(false); }
  };

  const loadLibrary = async () => {
    setLibraryOpen(true);
    setLibraryLoading(true);
    setError("");
    try {
      const { data } = await api.get("/api/mux/assets?limit=50");
      setLibraryAssets(Array.isArray(data?.assets) ? data.assets : []);
    } catch (requestError) {
      setError(detail(requestError, "Mux library could not be synced."));
    } finally { setLibraryLoading(false); }
  };

  const linkAsset = async (asset) => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/api/mux/teaser/link", { video_id: videoId, asset_id: asset.id });
      setTeaser(data);
      setLibraryOpen(false);
      setUploadInfo(null);
      toast.success("Teaser media linked");
    } catch (requestError) {
      setError(detail(requestError, "This Mux asset could not be linked as a teaser."));
    } finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.patch(`/api/mux/teaser/${videoId}`, {
        title: teaser?.title || "",
        description: teaser?.description || "",
        status: teaser?.status || "draft",
        poster_media_id: teaser?.poster_media_id || null,
      });
      setTeaser(data);
      toast.success("Teaser saved");
    } catch (requestError) {
      setError(detail(requestError, "Teaser could not be saved."));
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Remove this teaser?")) return;
    setBusy(true);
    setError("");
    try {
      await api.delete(`/api/mux/teaser/${videoId}`);
      setTeaser(null);
      setUploadInfo(null);
      toast.success("Teaser removed");
    } catch (requestError) {
      setError(detail(requestError, "Teaser could not be removed."));
    } finally { setBusy(false); }
  };

  const updateField = (field, value) => setTeaser((current) => ({
    ...(current || { status: "draft" }),
    [field]: value,
  }));
  const canPublish = teaser?.mux_status === "ready" && Boolean(teaser?.playback_id);
  const hasTeaser = Boolean(teaser?.playback_id || teaser?.upload_id || teaser?.title || teaser?.description || teaser?.poster_media_id);

  return (
    <section className="min-w-0 space-y-3 rounded-xl border border-pink-500/30 bg-black/40 p-4" aria-labelledby="manage-video-teaser-heading" data-testid="manage-video-teaser">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 id="manage-video-teaser-heading" className="text-lg font-bold text-[#FF10F0]">Teaser</h3>
          <p className="mt-1 text-xs text-zinc-400">Separate from the Trailer. Public display requires Ready or Published status.</p>
        </div>
        {loading && <LoaderCircle aria-label="Loading teaser" size={18} className="animate-spin text-pink-300" />}
      </div>

      {error && <p role="alert" className="rounded-lg border border-red-500/40 bg-red-950/20 p-3 text-sm text-red-200">{error}</p>}
      {!loading && !error && !hasTeaser && <p className="rounded-lg border border-dashed border-zinc-700 p-3 text-sm text-zinc-400">No teaser yet. Upload managed media or select a ready public asset from the Mux library.</p>}
      {teaser?.mux_status && <p className="text-xs text-zinc-400" role="status">Media status: {teaser.mux_status}{teaser.duration ? ` · ${Math.round(teaser.duration)} seconds` : " · duration unavailable"}</p>}
      {teaser?.playback_id && <div className="overflow-hidden rounded-lg border border-white/10 bg-black"><MuxPlayer playbackId={teaser.playback_id} streamType="on-demand" aria-label="Teaser preview" className="aspect-video w-full" accent-color="#FF10F0" /></div>}
      {!teaser?.playback_id && !loading && <div className="flex min-h-24 flex-col items-center justify-center rounded-lg border border-white/10 bg-zinc-950 text-center text-sm text-zinc-500">Teaser preview unavailable until media is ready.</div>}

      <div className="grid min-w-0 gap-3 sm:grid-cols-2">
        <label className="block min-w-0 text-sm text-zinc-300" htmlFor={`teaser-title-${videoId}`}>
          Title
          <input id={`teaser-title-${videoId}`} className={`${inputCls} mt-1 min-h-11`} value={teaser?.title || ""} maxLength={160} onChange={(event) => updateField("title", event.target.value)} />
        </label>
        <label className="block min-w-0 text-sm text-zinc-300" htmlFor={`teaser-status-${videoId}`}>
          Visibility / status
          <select id={`teaser-status-${videoId}`} className={`${inputCls} mt-1 min-h-11`} value={teaser?.status || "draft"} onChange={(event) => updateField("status", event.target.value)}>
            {STATUSES.map(([value, label]) => <option key={value} value={value} disabled={(["ready", "published"].includes(value) && !canPublish)}>{label}</option>)}
          </select>
        </label>
      </div>
      <label className="block text-sm text-zinc-300" htmlFor={`teaser-description-${videoId}`}>
        Description <span className="text-xs text-zinc-500">(optional)</span>
        <textarea id={`teaser-description-${videoId}`} className={`${inputCls} mt-1 min-h-20 resize-y`} maxLength={1000} value={teaser?.description || ""} onChange={(event) => updateField("description", event.target.value)} />
      </label>

      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className={`${btnGhost} ${teaserButtonClass}`} disabled={busy || loading} onClick={startUpload}><Upload size={14} className="mr-1 inline" />Upload / Replace Media</button>
        <button type="button" className={`${btnGhost} ${teaserButtonClass}`} disabled={busy || loading} onClick={loadLibrary}>Choose from Mux Library</button>
        <button type="button" className={`${btnGhost} ${teaserButtonClass}`} disabled={busy || loading} onClick={() => setPosterOpen(true)}><Image size={14} className="mr-1 inline" />{teaser?.poster_url ? "Change poster" : "Choose poster"}</button>
        {teaser?.upload_id && <button type="button" className={`${btnGhost} ${teaserButtonClass}`} disabled={busy} onClick={refresh}>Refresh media status</button>}
        {hasTeaser && <button type="button" className={`${btnGhost} ${teaserButtonClass} text-red-300`} disabled={busy || loading} onClick={remove}><Trash2 size={14} className="mr-1 inline" />Remove teaser</button>}
        <button type="button" className={`${btnPrimary} ${teaserButtonClass} ml-auto`} disabled={busy || loading} onClick={save}>{busy ? "Saving…" : "Save Teaser"}</button>
      </div>

      {teaser?.poster_url && <div className="flex items-center gap-3 rounded-lg border border-white/10 bg-zinc-950 p-2"><img src={teaser.poster_url} alt="Selected teaser poster" className="h-14 w-24 rounded object-cover" /><span className="text-xs text-zinc-300">Media Gallery poster</span><button type="button" className={`${btnGhost} ${teaserButtonClass} ml-auto`} onClick={() => { updateField("poster_media_id", null); updateField("poster_url", null); }}>Remove poster</button></div>}
      {uploadInfo && <div className="rounded-lg border border-dashed border-pink-500/40 bg-zinc-950/60 p-3"><MuxUploader endpoint={uploadInfo.url} onSuccess={async () => { setUploadInfo(null); await refresh(); }} onError={(event) => setError(event?.message || "Teaser upload failed. Retry or choose another media asset.")} style={{ "--uploader-background-color": "#111827", "--uploader-text-color": "#fff", "--uploader-border-color": "#FF10F0", "--button-background-color": "#FF10F0", "--button-text-color": "#000", "--progress-bar-fill-color": "#FF10F0" }} /></div>}
      {libraryOpen && <div className="space-y-2 rounded-lg border border-white/10 bg-zinc-950 p-3" aria-label="Mux teaser library">
        <div className="flex items-center justify-between gap-2"><h4 className="font-semibold text-white">Ready public Mux assets</h4><button type="button" className={`${btnGhost} ${teaserButtonClass}`} onClick={() => setLibraryOpen(false)}>Close</button></div>
        {libraryLoading ? <p role="status" className="text-sm text-zinc-400">Loading Mux library…</p> : libraryAssets.length === 0 ? <p className="text-sm text-zinc-400">No Mux assets found.</p> : <div className="max-h-48 space-y-2 overflow-y-auto">{libraryAssets.map((asset) => <button key={asset.id} type="button" className={`${btnGhost} ${teaserButtonClass} flex w-full items-center justify-between gap-3 text-left`} onClick={() => linkAsset(asset)} disabled={busy}><span className="min-w-0 truncate">{asset.title || asset.id}</span><span className="shrink-0 text-xs text-zinc-400">{asset.status}</span></button>)}</div>}
      </div>}
      <MediaGalleryPicker isOpen={posterOpen} currentUrl={teaser?.poster_url || ""} onClose={() => setPosterOpen(false)} onSelect={(url) => updateField("poster_url", url)} onSelectMedia={(media) => { updateField("poster_media_id", media.id); updateField("poster_url", media.thumbnail_url || media.image_url); }} />
    </section>
  );
}